import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CONTENT_PAGES } from "./pages/registry";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { ContentProvider } from "./context/ContentContext";
import { EditBar } from "./components/edit/EditBar";
import Header from "./components/Header";
import Footer from "./components/Footer";
import GoldBanner from "./components/GoldBanner";
import CartDrawer from "./components/CartDrawer";
import { ConsentBanner } from "./components/ConsentBanner";
import WelcomeBanner from "./components/WelcomeBanner";
import Home from "./pages/Home";
import BusinessPage from "./pages/BusinessPage";
import AuthCallback from "./pages/AuthCallback";
import { MusicPlayerProvider } from "./context/MusicPlayerContext";
import { GlobalMusicBar } from "./components/music/Queue";
import NotFound from "./pages/NotFound";
import { BetaGate } from "./components/BetaGate";
import { SeoModeGuard } from "./components/SeoModeGuard";
import { PassportHandoff } from "./components/PassportHandoff";
import { PassportNotice } from "./components/PassportNotice";
import BogoModal from "./components/BogoModal";
import GiveawayPage from "./pages/GiveawayPage";
import { LegacyRedirect } from "./components/LegacyRedirect";
import { ConversionCardRedirect } from "./components/ConversionCardRedirect";
import { useParams } from "react-router-dom";

// /product/<slug> → the live dish page when it exists; DishPage itself sends dead slugs on to /store/restaurant.
function ProductSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/menu/${slug}`} replace />;
}
import DishPage from "./pages/DishPage";
import TagPage from "./pages/TagPage";
import "./App.css";

// Route chunks: everything except Home, the store page and the shell loads per page.
const CustomPage = lazy(() => import("./pages/CustomPage"));
const JewelryPage = lazy(() => import("./pages/JewelryPage"));
const FoodCheckout = lazy(() => import("./pages/FoodCheckout"));
const OfferLanding = lazy(() => import("./pages/OfferLanding"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const ReferralLanding = lazy(() => import("./pages/ReferralLanding"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ClubPage = lazy(() => import("./pages/ClubPage"));
const ConfigPage = lazy(() => import("./pages/ConfigPage"));
const MealBuilderPage = lazy(() => import("./pages/MealBuilderPage"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const PlacePage = lazy(() => import("./pages/PlacePage"));
const MithaiPage = lazy(() => import("./pages/MithaiPage"));
const DiwaliPage = lazy(() => import("./pages/DiwaliPage"));
const StoryPage = lazy(() => import("./pages/StoryPage"));
const DietaryPage = lazy(() => import("./pages/DietaryPage"));
const DietaryInfoPage = lazy(() => import("./pages/DietaryInfoPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const MusicPage = lazy(() => import("./pages/MusicPage"));
const SongPage = lazy(() => import("./pages/SongPage"));
const AlbumPage = lazy(() => import("./pages/AlbumPage"));
const ReservationsPage = lazy(() => import("./pages/ReservationsPage"));
const CateringPage = lazy(() => import("./pages/CateringPage"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const LocalSeoPage = lazy(() => import("./pages/LocalSeoPage"));
const BetaAccess = lazy(() => import("./pages/BetaAccess"));

function Shell({ children }) {
  return (
    <div className="ut-theme min-h-screen flex flex-col">
      <WelcomeBanner />
      <Header />
      <GoldBanner />
      <CartDrawer />
      <ConsentBanner />
      <PassportNotice />
      <BogoModal />
      <div className="flex-1 min-h-[50vh]">{children}</div>
      <Footer />
      <EditBar />
    </div>
  );
}

// While a route chunk loads on first paint, keep showing the pre-rendered body (crawler snapshots must never catch an empty root).
const PRERENDERED = (() => {
  const el = typeof document !== "undefined" ? document.querySelector("#root .pr") : null;
  return el ? { path: window.location.pathname, html: el.outerHTML } : null;
})();
function PrerenderedFallback() {
  const { pathname } = useLocation();
  if (PRERENDERED && PRERENDERED.path === pathname) return <div className="min-h-[50vh]" dangerouslySetInnerHTML={{ __html: PRERENDERED.html }} />;
  return <div className="min-h-[50vh]" />;
}

// Client-side redirect that keeps the query string (printed QR links often carry utm params).
function GoTo({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search || ""}`} replace />;
}

// Legacy Owner.com (tryurbanturban.com) URL patterns -> permanent homes on this site.
// /tags/* and /places/* resolve per-URL via data/legacy_redirects.json (LegacyRedirect).
const LEGACY_REDIRECTS = [
  { path: "/menu", to: "/store/restaurant" },
  { path: "/chicago", to: "/" },
  { path: "/order", to: "/store/restaurant" },
  { path: "/order-online", to: "/store/restaurant" },
  { path: "/table-menu", to: "/store/restaurant" },
  { path: "/tablemenu", to: "/store/restaurant" },
  { path: "/table", to: "/store/restaurant" },
  { path: "/menu/vegetable-pakora", to: "/store/restaurant" },
  { path: "/menu/amritsari-fish-pakora", to: "/store/restaurant" },
  { path: "/menu/jalebi-with-rabri", to: "/store/restaurant" },
  { path: "/menu/gulab-jamun-cheesecake-cup-2", to: "/store/restaurant" },
  { path: "/page/contact", to: "/locations/halsted-chicago" },
];

const CANONICAL_HOST = "tryurbanturban.com";
const HOST_PINNED_PREFIXES = ["/dashboard", "/login"];

// Back office lives on one host: www.tryurbanturban.com/dashboard -> tryurbanturban.com/dashboard (once, before render).
function pinDashboardHost() {
  const { hostname, pathname, search, hash, protocol } = window.location;
  if (hostname === `www.${CANONICAL_HOST}` && HOST_PINNED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    window.location.replace(`${protocol}//${CANONICAL_HOST}${pathname}${search}${hash}`);
    return true;
  }
  return false;
}

function AppRouter() {
  const location = useLocation();
  if (pinDashboardHost()) {
    return null;
  }
  // Detect session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<PrerenderedFallback />}>
    <Routes>
      <Route path="/" element={<Shell><Home /></Shell>} />
      <Route path="/store/halwai/:sweet" element={<Shell><MithaiPage /></Shell>} />
      <Route path="/diwali-mithai-chicago" element={<Shell><DiwaliPage /></Shell>} />
      <Route path="/store/:slug" element={<Shell><BusinessPage /></Shell>} />
      <Route path="/halwai" element={<Navigate to="/store/halwai" replace />} />
      <Route path="/urban-halwai" element={<Navigate to="/store/halwai" replace />} />
      <Route path="/store/bakery" element={<Navigate to="/store/halwai" replace />} />
      <Route path="/urban-bakery" element={<Navigate to="/store/halwai" replace />} />
      <Route path="/bakery" element={<Navigate to="/store/halwai" replace />} />
      <Route path="/bakery-chicago" element={<Navigate to="/store/halwai" replace />} />
      <Route path="/table/:slug/:table" element={<Shell><BusinessPage qrMode /></Shell>} />
      <Route path="/jewelry" element={<Shell><JewelryPage /></Shell>} />
      <Route path="/build-your-meal" element={<Shell><MealBuilderPage /></Shell>} />
      {CONTENT_PAGES.map((p) => <Route key={p.path} path={p.path} element={<Shell><ConfigPage page={p} /></Shell>} />)}
      <Route path="/locations/:slug" element={<Shell><LocationPage /></Shell>} />
      <Route path="/menu/:slug" element={<Shell><DishPage /></Shell>} />
      <Route path="/catering" element={<Shell><CateringPage /></Shell>} />
      <Route path="/feedback" element={<Shell><FeedbackPage /></Shell>} />
      <Route path="/checkout" element={<Shell><FoodCheckout /></Shell>} />
      <Route path="/offer/:code" element={<Shell><OfferLanding /></Shell>} />
      <Route path="/referrals" element={<Shell><ReferralPage /></Shell>} />
      <Route path="/r/:code" element={<Shell><ReferralLanding /></Shell>} />
      <Route path="/payment/success" element={<Shell><PaymentSuccess /></Shell>} />
      <Route path="/payment/cancel" element={<Shell><PaymentCancel /></Shell>} />
      <Route path="/orders" element={<Shell><MyOrders /></Shell>} />
      <Route path="/club" element={<Shell><ClubPage /></Shell>} />
      <Route path="/giveaway" element={<Shell><GiveawayPage /></Shell>} />
      <Route path="/rules" element={<Shell><GiveawayPage /></Shell>} />
      <Route path="/giveaway-rules" element={<Shell><GiveawayPage /></Shell>} />
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard/*" element={<Dashboard />} />
      <Route path="/beta-access" element={<BetaAccess />} />
      <Route path="/tags/:slug" element={<TagPage />} />
      <Route path="/tags/*" element={<LegacyRedirect />} />
      <Route path="/places/:slug" element={<Shell><PlacePage /></Shell>} />
      <Route path="/places" element={<LegacyRedirect />} />
      <Route path="/p/:slug" element={<Shell><CustomPage /></Shell>} />
      <Route path="/product/:slug" element={<ProductSlugRedirect />} />
      <Route path="/story" element={<Navigate to="/our-story" replace />} />
      <Route path="/s/*" element={<ConversionCardRedirect />} />
      <Route path="/our-story" element={<Shell><StoryPage /></Shell>} />
      <Route path="/page/dietary-options" element={<Shell><DietaryPage /></Shell>} />
      <Route path="/dietary" element={<Shell><DietaryInfoPage /></Shell>} />
      <Route path="/faq" element={<Shell><FaqPage /></Shell>} />
      <Route path="/music" element={<MusicPage />} />
      <Route path="/music/album/:slug" element={<AlbumPage />} />
      <Route path="/music/:slug" element={<SongPage />} />
      <Route path="/reservations" element={<Shell><ReservationsPage /></Shell>} />
      <Route path="/devon" element={<Shell><LocationPage slugOverride="devon-avenue" /></Shell>} />
      {LEGACY_REDIRECTS.map((r) => (
        <Route key={r.path} path={r.path} element={<GoTo to={r.to} />} />
      ))}
      <Route path="/:seoSlug" element={<Shell><LocalSeoPage /></Shell>} />
      <Route path="*" element={<Shell><NotFound /></Shell>} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <BetaGate>
        <AuthProvider>
          <CartProvider>
            <ContentProvider>
            <MusicPlayerProvider>
              <SeoModeGuard />
              <PassportHandoff />
              <AppRouter />
              <GlobalMusicBar />
              <Toaster position="top-center" richColors />
            </MusicPlayerProvider>
            </ContentProvider>
          </CartProvider>
        </AuthProvider>
      </BetaGate>
    </BrowserRouter>
  );
}
